import type {
  MeetingConsentResponse,
  MeetingPublicView,
  RecordingStateView,
} from "./types";

/**
 * meet-service base URL. In dev Vite proxies /api → http://localhost:8010.
 * In prod the bundle is served from the same origin as the API, so the
 * relative path keeps working.
 */
const BASE = "/api";

class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body && typeof body.detail === "string") detail = body.detail;
    } catch {
      // body is not JSON — keep statusText
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function fetchPublicMeeting(
  shortCode: string,
  opts: { t?: string | null; tab?: string }
): Promise<MeetingPublicView> {
  const params = new URLSearchParams();
  if (opts.t) params.set("t", opts.t);
  if (opts.tab) params.set("tab", opts.tab);
  const qs = params.toString();
  return request<MeetingPublicView>(
    `/meetings/public/${shortCode}${qs ? "?" + qs : ""}`
  );
}

export function postConsent(
  shortCode: string,
  granted: boolean,
  opts: { t?: string | null }
): Promise<MeetingConsentResponse> {
  const params = new URLSearchParams();
  if (opts.t) params.set("t", opts.t);
  const qs = params.toString();
  return request<MeetingConsentResponse>(
    `/meetings/${shortCode}/consent${qs ? "?" + qs : ""}`,
    { method: "POST", body: JSON.stringify({ granted }) }
  );
}

export function startRecording(
  shortCode: string,
  t: string
): Promise<RecordingStateView> {
  const params = new URLSearchParams({ t });
  return request<RecordingStateView>(
    `/meetings/${shortCode}/recording/start?${params.toString()}`,
    { method: "POST", body: "{}" }
  );
}

export function stopRecording(
  shortCode: string,
  t: string
): Promise<RecordingStateView> {
  const params = new URLSearchParams({ t });
  return request<RecordingStateView>(
    `/meetings/${shortCode}/recording/stop?${params.toString()}`,
    { method: "POST" }
  );
}

export function endMeeting(
  shortCode: string,
  t: string
): Promise<{ status: string }> {
  const params = new URLSearchParams({ t });
  return request<{ status: string }>(
    `/meetings/${shortCode}/end?${params.toString()}`,
    { method: "POST" }
  );
}

export { ApiError };
